import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { safeCapture } from "../utils/analytics";
import {
  prewriteTopicSparks,
  headlineAnalyze,
  seoTitleMeta,
} from "../utils/api";
import { getProfile } from "../utils/profile";
import { getActiveBrand, getDefaultAudienceText } from "../utils/vault";
import BrandVoiceIndicator from "./BrandVoiceIndicator";

export default function PrewritingModule() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return <div style={{ padding: 24, opacity: 0.8 }}>Loading…</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const draftKey = `prewritingDraft:${user.id}`;

  const [tool, setTool] = useState("topicFinder");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [formData, setFormData] = useState({
    subject: "",
    keywords: "",
    headline: "",
    siteName: "",
    pageName: "",
    rewrite: false,
  });

  const [brandProfile, setBrandProfile] = useState(null);
  const [vaultBrand, setVaultBrand] = useState(null);
  const [vaultAudienceText, setVaultAudienceText] = useState("");

  useEffect(() => {
    if (!user) return;

    let isMounted = true;

    (async () => {
      try {
        const profile = await getProfile();
        if (!isMounted) return;
        setBrandProfile(profile);
      } catch (err) {
        console.warn("Could not load brand profile in PrewritingModule:", err);
      }

      try {
        const brand = await getActiveBrand();
        if (!isMounted) return;
        setVaultBrand(brand);
        if (brand?.name) {
          // Prefill only if the user hasn't already typed (or restored a
          // draft with) a site name, so manual entry always wins.
          setFormData((prev) => ({
            ...prev,
            siteName: prev.siteName || brand.name,
          }));
        }
        if (brand) {
          setVaultAudienceText(await getDefaultAudienceText(brand));
        }
      } catch (err) {
        console.warn("Could not load vault brand in PrewritingModule:", err);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const getSnarkLabel = (level) => {
    if (level < 0.2) return "Polite";
    if (level < 0.45) return "Light Edge";
    if (level < 0.7) return "Direct";
    return "Sharp";
  };

  const toolOptions = useMemo(
    () => [
      {
        value: "topicFinder",
        label: "Topic Sparks",
        description: "Generate pre-writing prompts and angles from a broad subject.",
      },
      {
        value: "headline",
        label: "Headline Analyzer",
        description: "Analyze a headline and optionally get rewrites.",
      },
      {
        value: "seoTitleMeta",
        label: "SEO Title + Meta",
        description: "Generate title tags and meta descriptions for a page.",
      },
    ],
    []
  );

  useEffect(() => {
    const saved = localStorage.getItem(draftKey);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      if (parsed?.tool) setTool(parsed.tool);
      if (parsed?.formData) {
        setFormData((prev) => ({
          ...prev,
          ...parsed.formData,
        }));
      }
      if (parsed?.result) setResult(parsed.result);
    } catch {
      // ignore bad localStorage state
    }
  }, [draftKey]);

  useEffect(() => {
    localStorage.setItem(
      draftKey,
      JSON.stringify({
        tool,
        formData,
        result,
      })
    );
  }, [draftKey, tool, formData, result]);

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const reset = () => {
    setError(null);
    setResult(null);
    setIsGenerating(false);

    setFormData({
      subject: "",
      keywords: "",
      headline: "",
      siteName: "",
      pageName: "",
      rewrite: false,
    });

    setTool("topicFinder");
    localStorage.removeItem(draftKey);
  };

  const renderResult = () => {
    if (result?.questions && Array.isArray(result.questions)) {
      return result.questions.join("\n");
    }

    if (result?.analysis) {
      const rewrites =
        result?.rewritten_headlines?.length
          ? "\n\nRewrites:\n" +
            result.rewritten_headlines.map((h) => `- ${h}`).join("\n")
          : "";

      return `Analysis:\n${result.analysis}\n\nNeeds rewrite: ${String(
        result.needs_rewrite
      )}${rewrites}`;
    }

    if (result?.titles && result?.metas) {
      const titles = result.titles
        .map((t, i) => `${i + 1}. ${t.text} (${t.chars})`)
        .join("\n");

      const metas = result.metas
        .map((m, i) => `${i + 1}. ${m.text} (${m.chars})`)
        .join("\n");

      return `Titles:\n${titles}\n\nMeta Descriptions:\n${metas}`;
    }

    return typeof result === "string"
      ? result
      : JSON.stringify(result, null, 2);
  };

  const copyPlainText = async () => {
    try {
      await navigator.clipboard.writeText(renderResult());
      alert("Copied!");
    } catch {
      alert("Could not copy to clipboard.");
    }
  };

  const handleRun = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (tool === "topicFinder" && !formData.subject.trim()) {
      setError("Please enter a general subject.");
      return;
    }

    if (tool === "headline" && !formData.headline.trim()) {
      setError("Please enter a headline to analyze.");
      return;
    }

    if (
      tool === "seoTitleMeta" &&
      (!formData.siteName.trim() ||
        !formData.pageName.trim() ||
        !formData.keywords.trim())
    ) {
      setError("Please enter site name, keywords, and the page name.");
      return;
    }

    setIsGenerating(true);

    try {
      let out = null;

      const voiceFields = {
        brand_voice: brandProfile?.brand_voice ?? "",
        snark_level: brandProfile?.snark_level ?? 0.3,
      };

      if (tool === "topicFinder") {
        out = await prewriteTopicSparks({
          subject: formData.subject.trim(),
          keywords: formData.keywords.trim(),
          audience: vaultAudienceText,
          email: user.email || "",
          user_id: user.id,
          mode: "prewriting",
          tool: "topicFinder",
          ...voiceFields,
        });
      } else if (tool === "headline") {
        out = await headlineAnalyze({
          headline: formData.headline.trim(),
          rewrite: !!formData.rewrite,
          company: vaultBrand?.name ?? "",
          audience: vaultAudienceText,
          email: user.email || "",
          user_id: user.id,
          mode: "prewriting",
          tool: "headline",
          ...voiceFields,
        });
      } else if (tool === "seoTitleMeta") {
        out = await seoTitleMeta({
          site_name: formData.siteName.trim(),
          keywords: formData.keywords.trim(),
          page_name: formData.pageName.trim(),
          email: user.email || "",
          user_id: user.id,
          mode: "prewriting",
          tool: "seoTitleMeta",
          ...voiceFields,
        });
      }

      setResult(out);
      safeCapture("prewriting_generated", { tool });
    } catch (err) {
      setError(err?.message || "Something went wrong.");
      safeCapture("prewriting_generation_failed", {
        tool,
        message: String(err?.message || "unknown_error").slice(0, 120),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const baseInputClass =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200";

  const labelClass = "mb-2 block text-sm font-medium text-slate-800";
  const helperClass = "mt-1 text-xs text-slate-500";

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
        <h1 className="text-2xl font-bold text-slate-900">Pre-Writing</h1>
        <p className="mt-2 text-slate-600">Think first. Write faster.</p>
      </div>

      <div className="mt-4">
        <BrandVoiceIndicator
          brandProfile={brandProfile}
          userEmail={user.email || ""}
          getSnarkLabel={getSnarkLabel}
        />
      </div>

      <form
        onSubmit={handleRun}
        className="mt-4 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8"
      >
        <div className="mb-6">
          <label htmlFor="tool" className={labelClass}>
            Choose a tool
          </label>
          <select
            id="tool"
            value={tool}
            onChange={(e) => {
              setTool(e.target.value);
              setError(null);
              setResult(null);
            }}
            className={baseInputClass}
          >
            {toolOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className={helperClass}>
            {
              toolOptions.find((option) => option.value === tool)?.description
            }
          </p>
        </div>

        {tool === "topicFinder" && (
          <div className="space-y-5">
            <div>
              <label htmlFor="subject" className={labelClass}>
                General subject
              </label>
              <input
                id="subject"
                type="text"
                value={formData.subject}
                onChange={(e) => updateField("subject", e.target.value)}
                placeholder="e.g. local SEO, trauma-aware productivity, spring gardening"
                className={baseInputClass}
              />
              <p className={helperClass}>
                Start broad. The tool will help uncover strong angles.
              </p>
            </div>

            <div>
              <label htmlFor="keywords" className={labelClass}>
                Keywords (optional)
              </label>
              <input
                id="keywords"
                type="text"
                value={formData.keywords}
                onChange={(e) => updateField("keywords", e.target.value)}
                placeholder="Comma-separated keywords"
                className={baseInputClass}
              />
              <p className={helperClass}>
                Optional, but useful when you already know related search terms
                or themes.
              </p>
            </div>
          </div>
        )}

        {tool === "headline" && (
          <div className="space-y-5">
            <div>
              <label htmlFor="headline" className={labelClass}>
                Headline to analyze
              </label>
              <textarea
                id="headline"
                rows={4}
                value={formData.headline}
                onChange={(e) => updateField("headline", e.target.value)}
                placeholder="Paste the headline you want analyzed"
                className={baseInputClass}
              />
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <input
                type="checkbox"
                checked={formData.rewrite}
                onChange={(e) => updateField("rewrite", e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-300"
              />
              <span>
                <span className="block text-sm font-medium text-slate-800">
                  Include rewrites
                </span>
                <span className="block text-xs text-slate-500">
                  Ask the tool to suggest improved headline options if needed.
                </span>
              </span>
            </label>
          </div>
        )}

        {tool === "seoTitleMeta" && (
          <div className="space-y-5">
            <div>
              <label htmlFor="siteName" className={labelClass}>
                Website name
              </label>
              <input
                id="siteName"
                type="text"
                value={formData.siteName}
                onChange={(e) => updateField("siteName", e.target.value)}
                placeholder="e.g. DobieCore, Bluedobie Developing"
                className={baseInputClass}
              />
            </div>

            <div>
              <label htmlFor="pageName" className={labelClass}>
                Page needing optimization
              </label>
              <input
                id="pageName"
                type="text"
                value={formData.pageName}
                onChange={(e) => updateField("pageName", e.target.value)}
                placeholder="e.g. Home, Services, About, Local SEO page"
                className={baseInputClass}
              />
            </div>

            <div>
              <label htmlFor="seoKeywords" className={labelClass}>
                Keywords
              </label>
              <textarea
                id="seoKeywords"
                rows={4}
                value={formData.keywords}
                onChange={(e) => updateField("keywords", e.target.value)}
                placeholder="Enter your target keywords, separated by commas"
                className={baseInputClass}
              />
              <p className={helperClass}>
                Include primary and supporting keywords if you have them.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={isGenerating}
            className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? "Working…" : "Get Results"}
          </button>

          <button
            type="button"
            onClick={reset}
            disabled={isGenerating}
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Reset
          </button>
        </div>
      </form>

      {result && (
        <div className="mt-4 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Result</h2>
            <button
              type="button"
              onClick={copyPlainText}
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Copy
            </button>
          </div>

          <pre className="mt-4 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-slate-800 overflow-x-auto">
            {renderResult()}
          </pre>
        </div>
      )}
    </div>
  );
}